import { WorkflowReviewLookupRequestForm } from '@app/generated/contracts/workflow-review-lookup-request.generated';
import { editableFormPayload, validateGeneratedForm, type FormIssues, type GeneratedFormValues } from '@template-ui/helpers/generated-form.helpers';
import type { WorkflowClientAction, WorkflowClientCommandModule } from './workflow-command-registry';
import { providedLookupRevisions, resolveProvidedLookupFields, workflowFormLookupBindingKey } from './workflow-form-lookup';

function requestInputIssues(action: WorkflowClientAction, values: GeneratedFormValues): FormIssues {
  const field = action.form?.configuration?.fields.find(field => field.name === 'workSummary');
  const value = values['workSummary'];
  return field?.control === 'select' && typeof value === 'string' && value !== value.trim()
    ? { workSummary: 'Choose a stored value without leading or trailing whitespace before sending this request.' } : {};
}

/** A separate exact Request v2 adapter owns its closed summary/revision transport. */
export const workflowReviewRequestLookupCommandModule: WorkflowClientCommandModule = {
  code: 'platform.review.request.lookup', version: 1, commands: [{
    key: { provider: 'workflow', kind: 'command', code: 'workflow.review.request', version: 2 },
    form: WorkflowReviewLookupRequestForm,
    endpointSuffix: '/review/request/v2',
    submitLabel: 'Send review request',
    submissionHint: 'Sends the submitted work for independent review.',
    confirmation: 'Review request saved.',
    supportsProvidedLookups: true,
    validateInput: requestInputIssues,
    bindContext: context => ({ occurrenceId: context.occurrenceId, expectedETag: context.stepETag }),
    draftContextKey: action => {
      const binding = action.form?.configuration?.fields.find(field => field.name === 'workSummary')?.lookup;
      return binding ? JSON.stringify(['provided', WorkflowReviewLookupRequestForm.contract, 'workSummary', workflowFormLookupBindingKey(binding)]) : null;
    },
    serializeInput: (form, values, action) => {
      if (!action?.form || Object.keys(validateGeneratedForm(form, values)).length || Object.keys(requestInputIssues(action, values)).length) return null;
      const resolved = resolveProvidedLookupFields(form, action.form.configuration, action.form.lookupFields);
      const revisions = resolved ? providedLookupRevisions(form, action.form.configuration, resolved.revisions, values) : null;
      if (!revisions || Object.keys(revisions).some(name => name !== 'workSummary')) return null;
      const input = editableFormPayload(form, values), field = form.fields.find(field => field.name === 'workSummary');
      if (typeof input['workSummary'] !== 'string') return null;
      return { workSummary: field?.control === 'select' ? input['workSummary'] : input['workSummary'].trim(),
        ...(revisions['workSummary'] ? { workSummaryLookupRevision: revisions['workSummary'] } : {}) };
    },
  }],
};
